// _shared/hooks/useBankSettings.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { bankService } from '../../_services/bankService';

export const useBankSettings = ({
  slug,
  notifyError = (msg) => console.error(msg),
  notifySuccess = (msg) => console.log(msg),
}) =>  {
  // 🔒 REFERENCIAS ESTABLES: No causan re-renders
  const notifyErrorRef = useRef(notifyError);
  const notifySuccessRef = useRef(notifySuccess);

  // Actualizar las refs cuando cambien las props
  useEffect(() => {
    notifyErrorRef.current = notifyError;
    notifySuccessRef.current = notifySuccess;
  }, [notifyError, notifySuccess]);

  const [initialLoading, setInitialLoading] = useState(true);

  // Datos de Bancos
  const [banks, setBanks] = useState([]);
  const [bankConfig, setBankConfig] = useState({ bank_id: '', account_id: '', authorization_id: '', extra_credentials: {} });
  const [useDefaultBank, setUseDefaultBank] = useState(true);
  const [isBankEnabled, setIsBankEnabled] = useState(true);
  const [isSavingBank, setIsSavingBank] = useState(false);
  const [isTogglingBank, setIsTogglingBank] = useState(false);

  // Datos de Stripe
  const [stripeConfig, setStripeConfig] = useState({ secret_key: '', publishable_key: '' });
  const [useDefaultStripe, setUseDefaultStripe] = useState(true);
  const [isStripeEnabled, setIsStripeEnabled] = useState(true);
  const [isSavingStripe, setIsSavingStripe] = useState(false);
  const [isTogglingStripe, setIsTogglingStripe] = useState(false);

  // Pruebas
  const [testQR, setTestQR] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);
  const [stripeTest, setStripeTest] = useState({ loading: false, error: null, success: false });

  const fetchConfigs = useCallback(async () => {
    if (!slug) return;
    setInitialLoading(true);
    try {
      const data = await bankService.getConfigs(slug);

      // Procesar configuración de Banco
      if (data?.bank) {
        setUseDefaultBank(data.bank.use_default ?? true);
        setIsBankEnabled(data.bank.is_enabled ?? true);

        if (!data.bank.use_default && data.bank.hasCustomConfig) {
          setBankConfig({
            bank_id: data.bank.bank_id || '',
            account_id: data.bank.config?.account_id || '',
            authorization_id: data.bank.config?.authorization_id || '',
            extra_credentials: data.bank.config?.extra_credentials || {}
          });
        } else {
          setBankConfig({ bank_id: '', account_id: '', authorization_id: '', extra_credentials: {} });
        }
      }

      // Procesar configuración de Stripe
      if (data?.stripe) {
        setUseDefaultStripe(data.stripe.use_default ?? true);
        setIsStripeEnabled(data.stripe.is_enabled ?? true);

        if (!data.stripe.use_default) {
          setStripeConfig({
            // ✅ MÁSCARA DE SEGURIDAD: El frontend nunca recibe la clave real
            secret_key: data.stripe.has_secret_key ? '••••••••••••••••' : '',
            publishable_key: data.stripe.publishable_key || ''
          });
        } else {
          setStripeConfig({ secret_key: '', publishable_key: '' });
        }
      }

      if (data?.banks) {
        setBanks(Array.isArray(data.banks) ? data.banks : [data.banks]);
      }
    } catch (error) {
      console.error("Error al obtener configuraciones:", error);
      notifyError("No se pudieron cargar las configuraciones de pago.");
    } finally {
      setInitialLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleStripeChange = (e) => {
    const { name, value } = e.target;
    setStripeConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveBank = async () => {
    setIsSavingBank(true);
    try {
      if (!useDefaultBank && (!bankConfig.bank_id || !bankConfig.account_id)) {
        notifyError("Por favor completa los datos del banco o selecciona 'Usar cuenta por defecto'.");
        return false;
      }

      await bankService.saveBankConfig(slug, {
        bank_id: useDefaultBank ? null : bankConfig.bank_id,
        account_id: useDefaultBank ? null : bankConfig.account_id?.trim(),
        authorization_id: useDefaultBank ? null : bankConfig.authorization_id?.trim(),
        extra_credentials: useDefaultBank ? null : bankConfig.extra_credentials,
        use_default: useDefaultBank,
      });

      notifySuccess("Configuración del banco guardada correctamente.");
      return true;
    } catch (error) {
      console.error("Error al guardar configuración del banco:", error);
      notifyError(error.response?.data?.error || error.response?.data?.message || "Error al guardar.");
      return false;
    } finally {
      setIsSavingBank(false);
    }
  };

  const handleSaveStripe = async () => {
    setIsSavingStripe(true);
    try {
      if (!useDefaultStripe) {
        const isSecretMasked = stripeConfig.secret_key === '••••••••••••••••';
        if ((!stripeConfig.secret_key?.trim() && !isSecretMasked) || !stripeConfig.publishable_key?.trim()) {
          notifyError('Por favor completa ambas llaves de Stripe.');
          return false;
        }
      }

      const payload = {
        useDefault: useDefaultStripe,
        publishable_key: useDefaultStripe ? null : stripeConfig.publishable_key?.trim(),
        // ✅ Si está enmascarada o es default, enviamos undefined para que el backend NO la sobrescriba
        secret_key: (useDefaultStripe || stripeConfig.secret_key === '••••••••••••••••') 
          ? undefined 
          : stripeConfig.secret_key?.trim()
      };

      await bankService.saveStripeConfig(slug, payload);
      notifySuccess("Configuración de Stripe guardada correctamente.");

      if (useDefaultStripe) {
        setStripeConfig({ secret_key: '', publishable_key: '' });
      }
      return true;
    } catch (error) {
      console.error("Error al guardar Stripe:", error);
      notifyError(error.response?.data?.error || error.response?.data?.message || "Error al guardar.");
      return false;
    } finally {
      setIsSavingStripe(false);
    }
  };

  const handleToggleBank = async () => {
    const newState = !isBankEnabled;
    setIsTogglingBank(true);
    try {
      await bankService.toggleBank(slug, newState);
      setIsBankEnabled(newState);
      notifySuccess(`Pagos por banco ${newState ? 'activados' : 'desactivados'}.`);
    } catch (error) {
      notifyError("Error al actualizar el estado del banco.");
    } finally {
      setIsTogglingBank(false);
    }
  };

  const handleToggleStripe = async () => {
    const newState = !isStripeEnabled;
    setIsTogglingStripe(true);
    try {
      await bankService.toggleStripe(slug, newState);
      setIsStripeEnabled(newState);
      notifySuccess(`Pagos por Stripe ${newState ? 'activados' : 'desactivados'}.`);
    } catch (error) {
      notifyError("Error al actualizar el estado de Stripe.");
    } finally {
      setIsTogglingStripe(false);
    }
  };

  const handleTestQR = async () => {
    setTestLoading(true);
    setTestError(null);
    setTestQR(null);
    try {
      const data = await bankService.testQR(slug);
      if (data?.qrData?.qr) {
        setTestQR(`data:image/png;base64,${data.qrData.qr}`);
        notifySuccess("QR de prueba generado (1 Bs).");
      } else if (data?.qrUrl) {
        setTestQR(data.qrUrl);
        notifySuccess("QR de prueba generado (1 Bs).");
      } else {
        throw new Error('Respuesta inesperada del servidor al generar QR.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setTestError(msg);
      notifyError(`Error QR: ${msg}`);
    } finally {
      setTestLoading(false);
    }
  };

  const handleTestStripe = async () => {
    setStripeTest({ loading: true, error: null, success: false });
    try {
      const data = await bankService.testStripe(slug);
      if (data?.success) {
        setStripeTest({ loading: false, success: true, error: null });
        notifySuccess("Conexión con Stripe verificada correctamente.");
      } else {
        throw new Error(data?.message || 'Fallo de autenticación con Stripe');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setStripeTest({ loading: false, success: false, error: msg });
      notifyError(`Error Stripe: ${msg}`);
    }
  };

  return {
    initialLoading, banks, bankConfig, stripeConfig,
    useDefaultBank, useDefaultStripe, isBankEnabled, isStripeEnabled,
    isSavingBank, isSavingStripe, isTogglingBank, isTogglingStripe,
    testQR, testLoading, testError, stripeTest,
    setUseDefaultBank, setUseDefaultStripe, setBankConfig, setStripeConfig,
    handleBankChange, handleStripeChange, handleSaveBank, handleSaveStripe,
    handleToggleBank, handleToggleStripe, handleTestQR, handleTestStripe
  };
};